/**
 * The `data:` payloads of a `text/event-stream` body, read by hand (`KLQ-20260905-001` §5a).
 *
 * The WHATWG event-stream grammar, cut to what the search stream sends: lines end in `\n`,
 * `\r\n` or `\r`; a blank line dispatches the event; `data:` lines are joined with `\n`; a line
 * starting with `:` is a comment (FastAPI's keep-alive ping) and is skipped; `event:`, `id:` and
 * `retry:` are ignored, because every search event names itself inside its JSON (phase 4, Notes
 * item 1) and nothing here reconnects.
 *
 * Aborting `signal` cancels the reader, so a superseded search stops reading at once rather than
 * draining a body nobody will render.
 */

const LINE_BREAK = /\r\n|\r|\n/;

export async function* readEventStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
): AsyncGenerator<string, void, undefined> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const onAbort = (): void => {
    reader.cancel().catch(() => undefined);
  };
  signal.addEventListener('abort', onAbort, { once: true });

  let buffer = '';
  let data: string[] = [];

  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (signal.aborted) return;
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      // A `\r` at the very end may be the first half of a `\r\n` split across two chunks;
      // reading it as a break now would dispatch an event in the middle of its lines.
      const heldCarriageReturn = !done && buffer.endsWith('\r');
      const complete = heldCarriageReturn ? buffer.slice(0, -1) : buffer;
      const lines = complete.split(LINE_BREAK);
      // The last piece has no break after it yet, unless the body has ended.
      const partial = done ? '' : (lines.pop() ?? '');
      buffer = heldCarriageReturn ? `${partial}\r` : partial;
      if (done) lines.push('');

      for (const line of lines) {
        if (line === '') {
          if (data.length > 0) yield data.join('\n');
          data = [];
        } else if (line.startsWith('data:')) {
          const rest = line.slice(5);
          data.push(rest.startsWith(' ') ? rest.slice(1) : rest);
        }
        // `:comment`, `event:`, `id:`, `retry:` — nothing to do.
      }
      if (done) return;
    }
  } finally {
    signal.removeEventListener('abort', onAbort);
    try {
      reader.releaseLock();
    } catch {
      // A read still pending after a cancel; the stream is going away either way.
    }
  }
}
